import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoAdd, IoTrash, IoPencil, IoPeople, IoNotificationsOutline } from 'react-icons/io5';
import moment from 'moment';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './GestionAvisosScreen.css';

function GestionAvisosScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!userData?.condominioId) return;

    setLoading(true);
    const q = query(
      collection(db, "avisos"),
      where("condominioId", "==", userData.condominioId),
      orderBy("fechaCreacion", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaAvisos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fechaCreacion: doc.data().fechaCreacion.toDate()
      }));
      setAvisos(listaAvisos);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  const handleEliminar = async (avisoId, avisoTitulo) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el aviso "${avisoTitulo}"? Esta acción no se puede deshacer.`)) {
      try {
        await deleteDoc(doc(db, "avisos", avisoId));
        // alert("Aviso eliminado."); // Feedback visual es suficiente
      } catch (e) {
        console.error("Error al eliminar: ", e);
        alert("Error al eliminar el aviso.");
      }
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-avisos" />
      
      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />

        <div className="ks-scroll-area">
          <div className="avisos-dashboard-container">
            {/* --- Premium Banner --- */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <h1>Gestión de Avisos</h1>
                  <p>Publica comunicados importantes a la comunidad.</p>
                  
                  <button
                    onClick={() => navigate('/admin-avisos/crear')}
                    className="hero-publish-btn"
                  >
                    <IoAdd size={20} />
                    <span>Nuevo</span>
                  </button>
                </div>

                <IoNotificationsOutline className="muro-hero-icon" />
              </div>
            </div>

            {/* --- Main Card --- */}
            <div className="avisos-card-list">

          {loading && (
            <div className="empty-state">
              <p>Cargando avisos...</p>
            </div>
          )}

          {!loading && avisos.length === 0 && (
            <div className="empty-state">
              <IoNotificationsOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
              <p>No hay avisos publicados aún.</p>
              <button
                onClick={() => navigate('/admin-avisos/crear')}
                style={{ marginTop: 15, color: '#e11d48', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: 600 }}
              >
                ¡Publica el primero!
              </button>
            </div>
          )}

          {!loading && avisos.length > 0 && (
            isMobile ? (
              <div className="mobile-avisos-list">
                {avisos.map((aviso) => (
                  <div key={aviso.id} className="mobile-aviso-card">
                    <div className="mac-header">
                      <span className="mac-title">{aviso.titulo}</span>
                      <div className="mac-actions">
                        <button
                          className="aviso-action-btn edit"
                          title="Editar Aviso"
                          onClick={() => navigate(`/admin-avisos/editar/${aviso.id}`)}
                        >
                          <IoPencil size={18} />
                        </button>
                        <button
                          className="aviso-action-btn delete"
                          title="Eliminar Aviso"
                          onClick={() => handleEliminar(aviso.id, aviso.titulo)}
                        >
                          <IoTrash size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="mac-body">
                      <span className="mac-date">{moment(aviso.fechaCreacion).format('D MMM YYYY, h:mm a')}</span>
                      <span className="mac-author">Por: {aviso.creadoPorNombre || 'Admin'}</span>
                      <span className="mac-author">Audiencia: {aviso.audiencia === 'residentes' ? 'Solo Residentes' : aviso.audiencia === 'vigilantes' ? 'Solo Vigilantes' : 'Todos'}</span>
                      <span className="leido-badge">
                        <IoPeople size={14} />
                        {aviso.leidoPor?.length || 0} vistos
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Fecha de Publicación</th>
                      <th>Publicado Por</th>
                      <th>Audiencia</th>
                      <th>Alcance</th>
                      <th style={{ textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {avisos.map((aviso) => (
                      <tr key={aviso.id}>
                        <td style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827' }}>
                          {aviso.titulo}
                        </td>
                        <td className="text-muted">
                          {moment(aviso.fechaCreacion).format('D MMM YYYY, h:mm a')}
                        </td>
                        <td className="text-muted">
                          {aviso.creadoPorNombre || 'Admin'}
                        </td>
                        <td>
                          <span className="type-badge" style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: '6px', background: '#f3f4f6', color: '#4b5563', fontWeight: 600 }}>
                            {aviso.audiencia === 'residentes' ? 'Solo Residentes' : aviso.audiencia === 'vigilantes' ? 'Solo Vigilantes' : 'Todos'}
                          </span>
                        </td>
                        <td>
                          <span className="leido-badge">
                            <IoPeople size={14} />
                            {aviso.leidoPor?.length || 0} vistos
                          </span>
                        </td>
                        <td>
                          <div className="action-cell-wrapper">
                            {/* Edit Button */}
                            <button
                              className="aviso-action-btn edit"
                              title="Editar Aviso"
                              onClick={() => navigate(`/admin-avisos/editar/${aviso.id}`)}
                            >
                              <IoPencil size={18} />
                            </button>

                            {/* Delete Button */}
                            <button
                              className="aviso-action-btn delete"
                              title="Eliminar Aviso"
                              onClick={() => handleEliminar(aviso.id, aviso.titulo)}
                            >
                              <IoTrash size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
            </div>
          </div>
        </div>
      </main>

      <KondoBottomNav activeRoute="/gestion-avisos" />
    </div>
  );
}

export default GestionAvisosScreen;